import { get, post } from '../request';
import { defaultPageSize } from '../../constants';

export async function fetchSearchOptions(
  { equityfinderService },
  authenticationToken,
) {
  const url = `${equityfinderService}/equityfinder/config/codes`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchSearchRules(
  { equityfinderService },
  authenticationToken,
) {
  const url = `${equityfinderService}/equityfinder/config/propertysearch`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchFields(
  { equityfinderService },
  authenticationToken,
) {
  const url = `${equityfinderService}/equityfinder/config/propertysearchresults `;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchCompGrid(
  { equityfinderService },
  authenticationToken,
  { propertyId, versionId, type },
) {
  const url = `${equityfinderService}/equityfinder/property/${propertyId}/compgrid/version/${versionId}/${type.toLowerCase()}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchPropertyCategories(
  { equityfinderService },
  authenticationToken,
) {
  const url = `${equityfinderService}/equityfinder/propcategorylist/summary`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchPropertyCategory(
  { equityfinderService },
  authenticationToken,
  { propertyId, year },
) {
  const url = `${equityfinderService}/equityfinder/property/${propertyId}/${year}/propcategory`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function searchPropertyComp(
  { equityfinderService },
  authenticationToken,
  { categoryList, propertyId, year },
) {
  const url = `${equityfinderService}/equityfinder/property/${propertyId}/${year}/comps/category`;
  const { body } = await post(authenticationToken, url, {
    categoryIDList: categoryList,
  });
  return body;
}

export async function searchProperty(
  { equityfinderService },
  authenticationToken,
  {
    page = 1,
    pageSize = defaultPageSize,
    sortField,
    sortDirection,
    filterModel,
    searchOptions,
    year = '2018',
    valueType = 'current',
  },
) {
  let url = `${equityfinderService}/equityfinder/property/search/${year}/${valueType}`;
  url += `?page=${page}&pageSize=${pageSize}`;
  if (sortField && sortDirection) {
    url += `&sortField=${sortField}&sortDirection=${sortDirection}`;
  }
  if (filterModel && Object.keys(filterModel).length >= 0) {
    Object.keys(filterModel).forEach(filter => {
      url += `&searchField=${filter}&searchText=${filterModel[filter].value}`;
    });
  }
  const { body } = await post(authenticationToken, url, searchOptions);
  return body;
}

export async function fetchAllProperty(
  { equityfinderService },
  authenticationToken,
  {
    sortField,
    sortDirection,
    filterModel,
    searchOptions,
    year = '2018',
    valueType = 'current',
  },
) {
  let url = `${equityfinderService}/equityfinder/property/search/${year}/${valueType}`;
  let isNeedAmp = true;
  if (sortField && sortDirection) {
    url += `?sortField=${sortField}&sortDirection=${sortDirection}`;
    isNeedAmp = false;
  }
  if (filterModel && Object.keys(filterModel).length >= 0) {
    url += isNeedAmp ? '?' : '&';
    Object.keys(filterModel).forEach(filter => {
      url += `searchField=${filter}&searchText=${filterModel[filter].value}`;
    });
  }
  const { body } = await post(authenticationToken, url, searchOptions);
  return body;
}

export async function fetchProperty(
  { equityfinderService },
  authenticationToken,
  { propertyId, year = '2018', valueType = 'current' },
) {
  const url = `${equityfinderService}/equityfinder/property/${propertyId}/${year}/${valueType}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchProtest(
  { equityfinderService },
  authenticationToken,
  { propertyId, year = '2018' },
) {
  const url = `${equityfinderService}/equityfinder/property/${propertyId}/${year}/protest`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchCompScore(
  { equityfinderService },
  authenticationToken,
  { propertyId, compPropertyId, propYear },
) {
  const url = `${equityfinderService}/equityfinder/property/${propertyId}/${propYear}/comparables/scorecomp/${compPropertyId}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function checkValidPropertyComp(
  { equityfinderService },
  authenticationToken,
  { propertyId, versionId },
) {
  const url = `${equityfinderService}/equityfinder/property/${propertyId}/compgrid/version/${versionId}/checkvalid`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function generateCompPDF(
  { equityfinderService },
  authenticationToken,
  { propertyId, year, versionId, gridType, persist, dataOnly },
) {
  const url = `${equityfinderService}/equityfinder/property/${propertyId}/${year}/${versionId}/reports/compgrid/${gridType}`;
  const { body } = await post(
    authenticationToken,
    url,
    { persist, dataOnly },
    { outputType: 'bytearray' },
  );
  return body;
}

export async function generateCoverPDF(
  { equityfinderService },
  authenticationToken,
  { propertyId, year, versionId, gridType, persist, dataOnly },
) {
  const url = `${equityfinderService}/equityfinder/property/${propertyId}/${year}/${versionId}/reports/compgrid/coversheet/${gridType}`;
  const { body } = await post(
    authenticationToken,
    url,
    { persist, dataOnly },
    { outputType: 'bytearray' },
  );
  return body;
}

export async function fetchPropertyScore(
  { equityfinderService },
  authenticationToken,
  { propertyId, versionId, gridType },
) {
  const url = `${equityfinderService}/equityfinder/property/${propertyId}/prodigyscore/version/${versionId}/${gridType}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function reorderVersionComps(
  { equityfinderService },
  authenticationToken,
  { versionId, compList },
) {
  const url = `${equityfinderService}/equityfinder/property/version/${versionId}/setorder`;
  const { body } = await post(authenticationToken, url, { compList });
  return body;
}

export const integrityCheck = async (
  { equityfinderService },
  authenticationToken,
  { propertyId, year },
) => {
  const url = `${equityfinderService}/equityfinder/property/${propertyId}/${year}/isvalid`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const generateGoogleMap = async (
  { equityfinderService },
  authenticationToken,
  { propertyId, year, versionId, gridType, persist },
) => {
  const url = `${equityfinderService}/equityfinder/property/${propertyId}/${year}/${versionId}/reports/map/google/${gridType}`;
  const { body } = await post(
    authenticationToken,
    url,
    {
      persist,
      dataOnly: false,
    },
    { outputType: 'bytearray' },
  );
  return body;
};

export const generateBingMap = async (
  { equityfinderService },
  authenticationToken,
  { propertyId, year, versionId, gridType, persist },
) => {
  const url = `${equityfinderService}/equityfinder/property/${propertyId}/${year}/${versionId}/reports/map/bing/${gridType}`;
  const { body } = await post(
    authenticationToken,
    url,
    {
      persist,
      dataOnly: false,
    },
    { outputType: 'bytearray' },
  );
  return body;
};
